import axios from 'axios'
import { ref, watch, onUnmounted } from 'vue'
import { getSearchPropertiesWithRetry } from '@/utils/apiBaseUrl'
import { getColivingListImageUrl } from '@/utils/colivingListImageUrl'
import { readColivingHubListCache } from '@/utils/colivingHubListCache'
import {
  readHeroFeaturedPoolCache,
  writeHeroFeaturedPoolCache,
} from '@/utils/heroFeaturedPoolCache'
import { tryFetchHubListSnapshot } from '@/services/colivingHubListApi'
import { isMarketplaceHiddenProvider } from '../../../shared/providerSearchBoost.cjs'

const MEMORY_CACHE_TTL_MS = 45 * 60 * 1000
const POOL_PAGE_SIZE = 12
const DEFAULT_PRIORITY_COUNT = 2
const DEFERRED_STAGGER_MS = 300
const LAZY_FALLBACK_MS = 3500
const HERO_SEARCH_TIMEOUT_MS = 12000

const memoryCache = new Map()
const inFlightByLocation = new Map()

function cacheGet(key) {
  const hit = memoryCache.get(key)
  if (!hit) return null
  if (Date.now() - hit.at > MEMORY_CACHE_TTL_MS) {
    memoryCache.delete(key)
    return null
  }
  return hit.data
}

function cacheSet(key, data) {
  memoryCache.set(key, { at: Date.now(), data })
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function normalizeLocationLabel(value) {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

function listingMatchesCityCard(listing, card) {
  const loc = normalizeLocationLabel(listing?.propertyLocation)
  if (!loc) return false
  const targets = card.searchLocations?.length
    ? card.searchLocations
    : [card.searchLocation || card.filterLocation || card.name]
  return targets.some((target) => {
    const t = normalizeLocationLabel(target)
    return loc === t || loc.includes(t) || t.includes(loc)
  })
}

function listingImageUrl(item) {
  const raw = item.imageUrl || item.propertyImage || item.coverImage || item.images?.[0] || ''
  return getColivingListImageUrl(raw, item.listImageUrl || '')
}

function listingDisplayPrice(item) {
  const priceFrom = Number(item.priceFrom)
  if (Number.isFinite(priceFrom) && priceFrom > 0) return priceFrom
  const propertyPrice = Number(item.propertyPrice)
  if (Number.isFinite(propertyPrice) && propertyPrice > 0) return propertyPrice
  const rooms = item.rooms || item.roomsConfiguration
  if (Array.isArray(rooms) && rooms.length > 0) {
    const prices = rooms
      .map((room) => Number(room.price))
      .filter((p) => Number.isFinite(p) && p > 0)
    if (prices.length) return Math.min(...prices)
  }
  return null
}

function isHeroListingCandidate(item) {
  if (!item || item.contractType !== 'coliving') return false
  if (item.assignedApplicant) return false
  if (item.isExternal && isMarketplaceHiddenProvider(item)) return false
  return Boolean(listingImageUrl(item))
}

function normalizeHeroListing(item) {
  const title = (item.title || item.propertyTitle || '').trim()
  return {
    id: item.id,
    title: title || null,
    imageUrl: listingImageUrl(item),
    price: listingDisplayPrice(item),
    propertyLocation: item.propertyLocation || '',
    raw: item,
  }
}

function poolFromRawResults(results) {
  const pool = []
  const seenIds = new Set()
  for (const item of results) {
    if (!isHeroListingCandidate(item)) continue
    const normalized = normalizeHeroListing(item)
    if (seenIds.has(normalized.id)) continue
    seenIds.add(normalized.id)
    pool.push(normalized)
  }
  return pool
}

function slimListingForStorage(normalized) {
  const r = normalized.raw || {}
  return {
    id: normalized.id,
    title: normalized.title,
    imageUrl: normalized.imageUrl,
    price: normalized.price,
    propertyLocation: normalized.propertyLocation,
    raw: {
      id: r.id ?? normalized.id,
      bookingIntegration: r.bookingIntegration,
      isExternal: r.isExternal,
      externalSource: r.externalSource,
      contractType: r.contractType ?? 'coliving',
      type: r.type,
    },
  }
}

function hydratePoolFromCache(pool) {
  return pool.map((entry) => ({
    ...entry,
    raw: {
      contractType: 'coliving',
      ...entry.raw,
    },
  }))
}

/** Misma lógica de destino que ContractCard (ficha o hub del operador). */
export function resolveHeroListingPath(listing, regionId) {
  if (!listing?.id) return null
  if (listing.bookingIntegration) {
    return `/colivings/${listing.bookingIntegration}/${listing.id}`
  }
  if (listing.isExternal && listing.externalSource) {
    const normalized = listing.externalSource.toLowerCase().trim()
    const prefix = regionId === 'us' || regionId === 'uk' ? 'findyourcoliving' : 'encuentratucoliving'
    return `/${prefix}/${normalized}`
  }
  if (listing.contractType === 'coliving' || listing.type === 'coliving') {
    return `/colivings/${listing.id}`
  }
  return null
}

async function fetchListingPoolForLocation({ location, region, lang, signal }) {
  const cacheKey = `${region}|${lang}|${location}|pool-v3`
  const cached = cacheGet(cacheKey)
  if (cached) return cached

  if (inFlightByLocation.has(cacheKey)) {
    return inFlightByLocation.get(cacheKey)
  }

  const apiUrl = process.env.VUE_APP_SEARCH_API_URL
  if (!apiUrl) return []

  const searchPath = region === 'uk' || region === 'us' ? '/findyourcoliving' : '/encuentratucoliving'

  const params = {
    contractType: 'coliving',
    includeMarketStats: 'false',
    pageSize: POOL_PAGE_SIZE,
    page: 1,
    region,
    lang: (lang || 'es').slice(0, 2),
    propertyLocation: location,
    sortBy: 'market_relevance',
    searchPath,
  }

  const fetchPromise = (async () => {
    try {
      const response = await getSearchPropertiesWithRetry(axios, apiUrl, {
        params,
        signal,
        timeout: HERO_SEARCH_TIMEOUT_MS,
      })
      const results = response?.data?.results
      if (!Array.isArray(results)) return []

      const pool = poolFromRawResults(results)
      if (pool.length) cacheSet(cacheKey, pool)
      return pool
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return []
      console.warn('[heroFeaturedListings]', location, err?.message || err)
      return []
    } finally {
      inFlightByLocation.delete(cacheKey)
    }
  })()

  inFlightByLocation.set(cacheKey, fetchPromise)
  return fetchPromise
}

async function fetchPoolForCityCard(card, region, lang, signal) {
  const locations = card.searchLocations?.length
    ? card.searchLocations
    : [card.searchLocation || card.filterLocation || card.name]

  const merged = []
  const seenIds = new Set()

  for (const location of locations) {
    const pool = await fetchListingPoolForLocation({ location, region, lang, signal })
    for (const item of pool) {
      if (seenIds.has(item.id)) continue
      seenIds.add(item.id)
      merged.push(item)
      if (merged.length >= POOL_PAGE_SIZE) return merged
    }
  }
  return merged
}

/**
 * Habitaciones reales del buscador: pool por ciudad, rotación, caché hub/session + snapshot rápido.
 */
export function useHeroFeaturedListings(regionId, locale, getCityCards, options = {}) {
  const priorityCount = options.priorityCount ?? DEFAULT_PRIORITY_COUNT
  const getLazyAnchor = options.getLazyAnchor

  const listingsPoolByCityId = ref({})
  const displayIndexByCityId = ref({})
  const isLoadingFeatured = ref(false)
  const isLoadingDeferred = ref(false)
  const loadingCityIds = ref({})

  const seenCityIds = new Set()
  let abortController = null
  let lazyObserver = null
  let lazyFallbackTimer = null
  let deferredStarted = false

  function resetSessionState() {
    seenCityIds.clear()
    listingsPoolByCityId.value = {}
    displayIndexByCityId.value = {}
    loadingCityIds.value = {}
    deferredStarted = false
  }

  function ensureDisplayIndex(cityId) {
    if (displayIndexByCityId.value[cityId] === undefined) {
      displayIndexByCityId.value = {
        ...displayIndexByCityId.value,
        [cityId]: 0,
      }
    }
  }

  function mergePoolIntoCity(cityId, incoming) {
    if (!incoming?.length) return
    const prev = listingsPoolByCityId.value[cityId] || []
    const seen = new Set(prev.map((p) => p.id))
    const merged = [...prev]
    for (const item of incoming) {
      if (seen.has(item.id)) continue
      seen.add(item.id)
      merged.push(item)
    }
    listingsPoolByCityId.value = {
      ...listingsPoolByCityId.value,
      [cityId]: merged.slice(0, POOL_PAGE_SIZE),
    }
    ensureDisplayIndex(cityId)
  }

  function persistCityPool(region, lang, cityId) {
    const pool = listingsPoolByCityId.value[cityId]
    if (!pool?.length) return
    writeHeroFeaturedPoolCache(
      region,
      lang,
      cityId,
      pool.map(slimListingForStorage),
    )
  }

  /** Pintura instantánea: sessionStorage del hero + hub cache del buscador. */
  function applyInstantSeeds(cards, region, lang) {
    let seeded = false

    for (const card of cards) {
      const sessionHit = readHeroFeaturedPoolCache(region, lang, card.id)
      if (sessionHit?.pool?.length) {
        listingsPoolByCityId.value = {
          ...listingsPoolByCityId.value,
          [card.id]: hydratePoolFromCache(sessionHit.pool),
        }
        ensureDisplayIndex(card.id)
        seeded = true
      }
    }

    const hubSession = readColivingHubListCache(region, 'market_relevance')
    if (hubSession?.results?.length) {
      for (const card of cards) {
        if (listingsPoolByCityId.value[card.id]?.length) continue
        const matched = hubSession.results.filter((r) => listingMatchesCityCard(r, card))
        const pool = poolFromRawResults(matched)
        if (pool.length) {
          mergePoolIntoCity(card.id, pool)
          seeded = true
        }
      }
    }

    return seeded
  }

  async function fetchAndApplyHubSnapshot(cards, region, signal) {
    try {
      const snap = await tryFetchHubListSnapshot({
        region,
        pageSize: 50,
        sortBy: 'market_relevance',
        signal,
      })
      if (!snap?.results?.length || signal.aborted) return

      for (const card of cards) {
        const matched = snap.results.filter((r) => listingMatchesCityCard(r, card))
        const pool = poolFromRawResults(matched)
        if (pool.length) mergePoolIntoCity(card.id, pool)
      }
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return
    }
  }

  function setCityLoading(cityId, loading) {
    const next = { ...loadingCityIds.value }
    if (loading) next[cityId] = true
    else delete next[cityId]
    loadingCityIds.value = next
  }

  function getDisplayedListing(cityId) {
    const pool = listingsPoolByCityId.value[cityId]
    if (!pool?.length) return null
    const idx = displayIndexByCityId.value[cityId] || 0
    return pool[idx % pool.length]
  }

  function getListingPoolMeta(cityId) {
    const pool = listingsPoolByCityId.value[cityId]
    if (!pool?.length) return null
    const idx = displayIndexByCityId.value[cityId] || 0
    return {
      current: (idx % pool.length) + 1,
      total: pool.length,
    }
  }

  function seedInitialCity(cityId) {
    if (cityId) seenCityIds.add(cityId)
  }

  function onHeroCitySelected(cityId) {
    if (seenCityIds.has(cityId)) {
      advanceCityListing(cityId)
    } else {
      seenCityIds.add(cityId)
    }
  }

  function advanceCityListing(cityId) {
    const pool = listingsPoolByCityId.value[cityId]
    if (!pool || pool.length < 2) return
    const cur = displayIndexByCityId.value[cityId] || 0
    displayIndexByCityId.value = {
      ...displayIndexByCityId.value,
      [cityId]: (cur + 1) % pool.length,
    }
  }

  function isCityPoolLoading(cityId) {
    return Boolean(loadingCityIds.value[cityId])
  }

  function cityPoolNeedsNetwork(card) {
    const len = listingsPoolByCityId.value[card.id]?.length || 0
    return len < POOL_PAGE_SIZE
  }

  /** Stale-while-revalidate: muestra caché y completa pool en red. */
  async function loadCityPool(card, region, lang, signal) {
    if (!cityPoolNeedsNetwork(card)) return
    if (loadingCityIds.value[card.id] && !listingsPoolByCityId.value[card.id]?.length) return

    const showSpinner = !listingsPoolByCityId.value[card.id]?.length
    if (showSpinner) setCityLoading(card.id, true)

    try {
      const apiPool = await fetchPoolForCityCard(card, region, lang, signal)
      if (signal.aborted) return
      if (apiPool.length) {
        mergePoolIntoCity(card.id, apiPool)
        persistCityPool(region, lang, card.id)
      }
    } finally {
      if (showSpinner) setCityLoading(card.id, false)
    }
  }

  function teardownLazyObserver() {
    if (lazyObserver) {
      lazyObserver.disconnect()
      lazyObserver = null
    }
    if (lazyFallbackTimer) {
      clearTimeout(lazyFallbackTimer)
      lazyFallbackTimer = null
    }
  }

  async function loadDeferredCities(cards, region, lang, signal) {
    if (deferredStarted || !cards.length) return
    deferredStarted = true
    isLoadingDeferred.value = true
    try {
      for (const card of cards) {
        if (signal.aborted) break
        if (cityPoolNeedsNetwork(card)) {
          await loadCityPool(card, region, lang, signal)
          await sleep(DEFERRED_STAGGER_MS)
        }
      }
    } finally {
      isLoadingDeferred.value = false
    }
  }

  function scheduleDeferredLoad(deferredCards, region, lang, signal) {
    teardownLazyObserver()

    const start = () => {
      loadDeferredCities(deferredCards, region, lang, signal)
    }

    const anchor = typeof getLazyAnchor === 'function' ? getLazyAnchor() : null
    if (!anchor || typeof IntersectionObserver === 'undefined') {
      start()
      return
    }

    lazyObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          teardownLazyObserver()
          start()
        }
      },
      { rootMargin: '80px', threshold: 0.08 },
    )
    lazyObserver.observe(anchor)

    lazyFallbackTimer = setTimeout(() => {
      teardownLazyObserver()
      start()
    }, LAZY_FALLBACK_MS)
  }

  async function loadFeaturedListings() {
    const apiUrl = process.env.VUE_APP_SEARCH_API_URL
    if (!apiUrl) return

    const cards = typeof getCityCards === 'function' ? getCityCards() : []
    if (!cards.length) return

    if (abortController) abortController.abort()
    abortController = new AbortController()
    const { signal } = abortController

    resetSessionState()
    teardownLazyObserver()

    const region = regionId.value || 'es'
    const lang = locale.value || 'es'
    const priorityCards = cards.slice(0, priorityCount)
    const deferredCards = cards.slice(priorityCount)

    applyInstantSeeds(cards, region, lang)

    const priorityHasData = priorityCards.some((c) => listingsPoolByCityId.value[c.id]?.length)
    isLoadingFeatured.value = !priorityHasData

    try {
      await Promise.all([
        fetchAndApplyHubSnapshot(cards, region, signal),
        ...priorityCards.map((card) => loadCityPool(card, region, lang, signal)),
      ])
    } finally {
      isLoadingFeatured.value = false
    }

    if (!signal.aborted && deferredCards.length) {
      scheduleDeferredLoad(deferredCards, region, lang, signal)
    }
  }

  watch(
    [
      regionId,
      locale,
      () => (typeof getCityCards === 'function' ? getCityCards().map((c) => c.id).join(',') : ''),
    ],
    () => {
      loadFeaturedListings()
    },
    { immediate: true },
  )

  onUnmounted(() => {
    if (abortController) abortController.abort()
    teardownLazyObserver()
  })

  return {
    listingsPoolByCityId,
    getDisplayedListing,
    getListingPoolMeta,
    onHeroCitySelected,
    advanceCityListing,
    isLoadingFeatured,
    isLoadingDeferred,
    isCityPoolLoading,
    reloadFeaturedListings: loadFeaturedListings,
    seedInitialCity,
  }
}
